import React from "react";
import Button from "./Button";

export const formatDateInputValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseStartOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const parseEndOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

const coerceToDate = (value, endOfDay = false) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return endOfDay ? parseEndOfDay(value) : parseStartOfDay(value);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const FilterBar = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  stores = [],
  storeId = "",
  onStoreChange,
  showStore = true,
  channel,
  onChannelChange,
  showChannel = false,
  channelOptions,
  showSearch = false,
  search = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  presets = [],
  activePreset = "",
  onPresetChange,
  showStatus = false,
  status = "",
  onStatusChange,
  statusOptions = [],
  statusLabel = "Status",
  limit,
  onLimitChange,
  showLimit = false,
  extraFilters,
  onApply,
  onRefresh,
  onReset,
  isLoading = false,
  validationMessage = "",
}) => {
  const resolvedChannelOptions =
    Array.isArray(channelOptions) && channelOptions.length > 0
      ? channelOptions
      : [
          { value: "KIOSK", label: "Kiosk" },
          { value: "POS", label: "POS" },
          { value: "ONLINE", label: "Online" },
          { value: "ALL", label: "All Channels" },
        ];

  const fromDate = coerceToDate(startDate);
  const toDate = coerceToDate(endDate, true);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      {presets.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {presets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => onPresetChange && onPresetChange(preset.value)}
              className={[
                "h-8 rounded-full border px-3 text-xs font-semibold transition",
                activePreset === preset.value
                  ? "border-indigo-400 bg-indigo-100 text-indigo-700"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[150px]">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            From
          </label>
          <input
            type="date"
            value={formatDateInputValue(fromDate)}
            max={formatDateInputValue(toDate)}
            onChange={(e) => onStartDateChange(parseStartOfDay(e.target.value))}
            className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
          />
        </div>

        <div className="min-w-[150px]">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            To
          </label>
          <input
            type="date"
            value={formatDateInputValue(toDate)}
            min={formatDateInputValue(fromDate)}
            onChange={(e) => onEndDateChange(parseEndOfDay(e.target.value))}
            className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
          />
        </div>

        {showStore && (
          <div className="min-w-[170px]">
            <label className="mb-1 block text-xs font-medium text-slate-500">
              Stores
            </label>
            <select
              value={storeId}
              onChange={(e) => onStoreChange && onStoreChange(e.target.value)}
              className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
            >
              <option value="">All synced stores</option>
              {stores.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                  {option.businessName ? ` (${option.businessName})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {showChannel && (
          <div className="min-w-[140px]">
            <label className="mb-1 block text-xs font-medium text-slate-500">
              Channel
            </label>
            <select
              value={channel}
              onChange={(e) => onChannelChange && onChannelChange(e.target.value)}
              className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
            >
              {resolvedChannelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {showSearch && (
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-500">
              Search
            </label>
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              value={search}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
            />
          </div>
        )}

        {showStatus && (
          <div className="min-w-[170px]">
            <label className="mb-1 block text-xs font-medium text-slate-500">
              {statusLabel}
            </label>
            <select
              value={status}
              onChange={(e) => onStatusChange && onStatusChange(e.target.value)}
              className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {showLimit && (
          <div className="w-[120px]">
            <label className="mb-1 block text-xs font-medium text-slate-500">
              Limit
            </label>
            <input
              type="text"
              inputMode="numeric"
              enterKeyHint="done"
              pattern="[0-9]*"
              value={limit}
              onChange={(e) => onLimitChange && onLimitChange(e.target.value)}
              className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
            />
          </div>
        )}

        {extraFilters}

        <div className="ml-auto flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="h-9 rounded border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Refresh
            </button>
          )}
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="h-9 rounded border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Reset
            </button>
          )}
          {onApply && (
            <Button size="sm" onClick={onApply} disabled={isLoading}>
              {isLoading ? "Loading..." : "Apply"}
            </Button>
          )}
        </div>
      </div>
      {validationMessage && (
        <p className="mt-2 text-xs font-medium text-rose-600">
          {validationMessage}
        </p>
      )}
    </div>
  );
};

export default FilterBar;
